<template>
  <sdPageHeader title="Laporan Pembinaan Person Grade"> </sdPageHeader>
  <Main>
    <sdCards headless>
      <!-- Search Section -->
      <div class="search-section">
        <a-input-search
          v-model="searchQuery"
          placeholder="Cari Sesuatu..."
          style="width: 250px"
          @search="handleSearch"
        />
        <div style="float: right">
          <a-button type="primary" @click="exportData" :loading="loadingExport">
            <template #icon><download-outlined /></template>
            {{ loadingExport ? 'Exporting...' : 'Export' }}
          </a-button>
        </div>
      </div>

      <!-- Table Section -->
      <a-table
        :columns="columns"
        :data-source="tableData"
        :loading="loading"
        :pagination="{
          total: pagination.total,
          current: pagination.current,
          pageSize: pagination.pageSize,
          showSizeChanger: pagination.showSizeChanger,
          pageSizeOptions: pagination.pageSizeOptions,
          showTotal: pagination.showTotal,
        }"
        @change="handleTableChange"
      >
        <template #status="{ text }">
          <a-tag :color="getStatusColor(text)">
            {{ text }}
          </a-tag>
        </template>
        <template #action="{ record }">
          <div class="action-buttons">
            <a-button type="link" shape="circle" @click="viewDetails(record)">
              <template #icon><eye-outlined /></template>
            </a-button>
          </div>
        </template>
      </a-table>
    </sdCards>
  </Main>
</template>

<script>
import { Main } from '../styled';
import { EyeOutlined, DownloadOutlined } from '@ant-design/icons-vue';
import assessmentApi from '@/api/assessment';
import { getItem } from '@/utility/localStorageControl';
import { handleSessionExpired } from '@/utility/utility';

const ViewPage = {
  name: 'Report',
  components: {
    Main,
    EyeOutlined,
    DownloadOutlined,
  },
  data() {
    return {
      searchQuery: '',
      loading: false,
      loadingExport: false,
      statusFilters: [],
      columns: [
        {
          title: 'No',
          dataIndex: 'no',
          key: 'no',
          width: 70,
        },
        {
          title: 'NIP',
          dataIndex: 'nip',
          key: 'nip',
        },
        {
          title: 'Nama Pegawai',
          dataIndex: 'namaPegawai',
          key: 'namaPegawai',
        },
        {
          title: 'PeG',
          dataIndex: 'peg',
          key: 'peg',
          width: 80,
        },
        {
          title: 'Jabatan',
          dataIndex: 'jabatan',
          key: 'jabatan',
        },
        {
          title: 'PoG Jabatan Existing',
          dataIndex: 'pog',
          key: 'pog',
        },
        {
          title: 'Proyeksi Jabatan',
          dataIndex: 'proyeksiJabatan',
          key: 'proyeksiJabatan',
        },
        {
          title: 'Personal Area',
          dataIndex: 'personalArea',
          key: 'personalArea',
        },
        {
          title: 'Status',
          dataIndex: 'status',
          key: 'status',
          slots: { customRender: 'status' },
          filters: [
            { text: 'Inisiasi Pengajuan', value: 'Inisiasi Pengajuan' },
            { text: 'Disetujui', value: 'Disetujui' },
            { text: 'Ditinjau Ulang', value: 'Ditinjau Ulang' },
            { text: 'Menunggu Approval Atasan', value: 'Menunggu Approval Atasan' },
            { text: 'Menunggu Approval BOD-1', value: 'Menunggu Approval BOD-1' },
            { text: 'Menunggu Approval HC', value: 'Menunggu Approval HC' },
            { text: 'Disetujui Oleh HC', value: 'Disetujui Oleh HC' },
            { text: 'Ditinjau Ulang Oleh HC', value: 'Ditinjau Ulang Oleh HC' },
          ],
          onFilter: (value, record) => record.status === value,
        },
        {
          title: 'Aksi',
          key: 'action',
          width: 80,
          slots: { customRender: 'action' },
        },
      ],
      tableData: [],
      pagination: {
        total: 1,
        current: 1,
        pageSize: 10,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50'],
        showTotal: (total, range) => `Menampilkan ${range[0]}-${range[1]} dari ${total} Data`,
        onChange: () => {}, // Empty function to be filled by handleTableChange
        onShowSizeChange: () => {}, // Empty function to be filled by handleTableChange
      },
    };
  },
  created() {
    this.fetchTableData();
  },
  methods: {
    fetchTableData() {
      this.loading = true;

      const accessToken = getItem('access_token');

      const headers = {};
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      // Prepare query parameters for pagination
      const params = {
        page: this.pagination.current,
        per_page: this.pagination.pageSize,
      };

      // Add search query if present - specifically for employee name
      if (this.searchQuery) {
        params.name = this.searchQuery.toUpperCase();
      }

      // Add status filters if any are selected
      if (this.statusFilters && this.statusFilters.length > 0) {
        // Convert display status values back to API status values
        const apiStatusValues = this.statusFilters.map(displayStatus => {
          switch (displayStatus) {
            case 'Inisiasi Pengajuan':
              return 'inisialisasi';
            case 'Disetujui':
              return 'disetujui';
            case 'Ditinjau Ulang':
              return 'ditolak';
            case 'Menunggu Approval Atasan':
              return 'menunggu_approval_atasan';
            case 'Menunggu Approval BOD-1':
              return 'menunggu_approval_bod1';
            case 'Menunggu Approval HC':
              return 'menunggu_approval_hc';
            case 'Disetujui Oleh HC':
              return 'disetujui_hc';
            case 'Ditinjau Ulang Oleh HC':
              return 'ditolak_hc';
            default:
              return displayStatus;
          }
        });
        params.status = apiStatusValues.join(',');
      }

      assessmentApi
        .get(`reporting?with=proyeksiJabatan,userAssessment`, {
          headers,
          params,
        })
        .then(response => {
          if (response.data && response.data.data && response.data.data.data) {
            const apiData = response.data.data.data;

            this.tableData = apiData.map((item, index) => {
              const proyeksi = item.proyeksi_jabatan || {};
              const userAssessment = item.user_assessment || {};
              return {
                key: item.id.toString(),
                no: index + 1,
                nip: item.nip || '',
                namaPegawai: userAssessment.name || '',
                peg: item.peg || '',
                jabatan: item.jabatan || '',
                // pog: proyeksi.pog ? proyeksi.pog.toString() : '',
                pog: item.peg || '',
                personalArea: userAssessment.personal_area || '',
                // proyeksiJabatan: proyeksi.jabatan || '',
                proyeksiJabatan: (proyeksi.pog ? proyeksi.pog + ' - ' : '') + (proyeksi.jabatan || ''),
                status: this.formatStatus(item.status),
                record: item,
              };
            });

            const paginationInfo = response.data.data;
            this.pagination = {
              ...this.pagination,
              total: paginationInfo.total || 0,
              current: paginationInfo.current_page || 1,
              pageSize: paginationInfo.per_page || 10,
              // Preserve the callback functions
              onChange: page => {
                this.pagination.current = page;
                this.fetchTableData();
              },
              onShowSizeChange: (current, size) => {
                this.pagination.current = current;
                this.pagination.pageSize = size;
                this.fetchTableData();
              },
            };
          }
        })
        .catch(error => {
          console.error('Error fetching report data:', error);
          this.$message.error('Gagal memuat data laporan');
          if (error.response && error.response.status === 401) {
            handleSessionExpired();
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    formatStatus(status) {
      // Format the status for display
      switch (status) {
        case 'inisialisasi':
          return 'Inisiasi Pengajuan';
        case 'disetujui':
          return 'Disetujui';
        case 'ditolak':
          return 'Ditinjau Ulang';
        case 'menunggu_approval_atasan':
          return 'Menunggu Approval Atasan';
        case 'menunggu_approval_bod1':
          return 'Menunggu Approval BOD-1';
        case 'menunggu_approval_hc':
          return 'Menunggu Approval HC';
        case 'disetujui_hc':
          return 'Disetujui Oleh HC';
        case 'ditolak_hc':
          return 'Ditinjau Ulang Oleh HC';
        default:
          return status || 'Unknown';
      }
    },

    getStatusColor(status) {
      // Customize the color based on different statuses
      switch (status) {
        case 'Inisiasi Pengajuan':
          return 'blue';
        case 'Disetujui':
          return 'green';
        case 'Ditinjau Ulang':
          return 'red';
        case 'Menunggu Approval Atasan':
          return 'orange';
        case 'Menunggu Approval BOD-1':
          return 'orange';
        case 'Menunggu Approval HC':
          return 'orange';
        case 'Disetujui Oleh HC':
          return 'green';
        case 'Ditinjau Ulang Oleh HC':
          return 'red';
        default:
          return 'default';
      }
    },

    handleSearch(value) {
      console.log('Searching for nama pegawai:', value);
      // Update search query to filter by employee name
      this.searchQuery = value;
      // Reset pagination to first page when searching
      this.pagination.current = 1;
      // Fetch data with the search parameter
      this.fetchTableData();
    },
    handleTableChange(pagination, filters, sorter) {
      console.log('Table params changed:', { pagination, filters, sorter });

      // Update pagination state
      this.pagination = {
        ...this.pagination,
        current: pagination.current,
        pageSize: pagination.pageSize,
        // Update the callback functions
        onChange: page => {
          this.pagination.current = page;
          this.fetchTableData();
        },
        onShowSizeChange: (current, size) => {
          this.pagination.current = current;
          this.pagination.pageSize = size;
          this.fetchTableData();
        },
      };

      // Store status filters
      this.statusFilters = filters.status || [];

      // Fetch data with updated pagination and filters
      this.fetchTableData();
    },
    viewDetails(record) {
      this.$router.push({ name: 'detail', params: { id: record.key } });
    },
    exportData() {
      this.loadingExport = true;
      const accessToken = getItem('access_token');

      // Create headers
      const headers = new Headers();
      if (accessToken) {
        headers.append('Authorization', `Bearer ${accessToken}`);
      }
      headers.append('Accept', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
      headers.append('Content-Type', 'application/json');

      // Make POST request without credentials
      fetch(`${process.env.VUE_APP_API_ASS}/export`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ with: ['proyeksiJabatan', 'userAssessment'] }),
      })
        .then(response => {
          if (!response.ok) throw new Error('Unauthorized');
          return response.blob();
        })
        .then(blob => {
          // Create download link
          const date = new Date();
          const offset = date.getTimezoneOffset();
          date.setMinutes(date.getMinutes() - offset);
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `report_${date
            .toISOString()
            .replace(/[-:T.]/g, '')
            .slice(0, 14)}.xlsx`;
          document.body.appendChild(a);
          a.click();

          // Clean up
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        })
        .catch(error => {
          this.$message.error('Export failed: ' + error.message);
        })
        .finally(() => {
          this.loadingExport = false;
        });
    },
  },
};

export default ViewPage;
</script>

<style scoped>
.search-section {
  margin-bottom: 20px;
}

.action-buttons {
  display: flex;
  justify-content: center;
}
</style>
